import { useState } from "react";
import { cn } from '@/lib/utils';
import { RESPONSIVE, SPACING } from '@/design-system';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Key, Mail, Wand2 } from "lucide-react";
import { useLocalizations } from "@/hooks/useLocalizations";
import { LCommon } from "@/components/i18n";

export type FAQActionType = 'password_reset' | 'resend_verification' | 'magic_link';

interface FAQActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionType: FAQActionType | null;
  defaultEmail?: string;
}

const ACTION_CONFIG: Record<FAQActionType, {
  successKey: string;
  defaultValue: string;
  icon: typeof Key;
}> = {
  password_reset: {
    successKey: 'auth.password_reset.success',
    defaultValue: 'Password reset link sent. Check your email.',
    icon: Key
  },
  resend_verification: {
    successKey: 'auth.resend_verification.success',
    defaultValue: 'Verification email sent. Check your inbox.',
    icon: Mail
  },
  magic_link: {
    successKey: 'auth.magic_link.success',
    defaultValue: 'Magic link sent. Check your email.',
    icon: Wand2
  },
};

// Helper to render action title with explicit fallbacks
const renderActionTitle = (actionType: FAQActionType) => {
  if (actionType === 'password_reset') return <LCommon id="auth.password_reset.title" defaultValue="Reset Password" />;
  if (actionType === 'resend_verification') return <LCommon id="auth.resend_verification.title" defaultValue="Resend Verification" />;
  if (actionType === 'magic_link') return <LCommon id="auth.magic_link.title" defaultValue="Sign In with Magic Link" />;
  return null;
};

// Helper to render action description with explicit fallbacks
const renderActionDescription = (actionType: FAQActionType) => {
  if (actionType === 'password_reset') return <LCommon id="auth.password_reset.description" defaultValue="Enter your email to reset your password." />;
  if (actionType === 'resend_verification') return <LCommon id="auth.resend_verification.description" defaultValue='We can resend your verification email. Please check your inbox and spam folder.' />;
  if (actionType === 'magic_link') return <LCommon id="auth.magic_link.description" defaultValue="Sign in with a magic link." />;
  return null;
};

// Helper to render action button with explicit fallbacks
const renderActionButton = (actionType: FAQActionType) => {
  if (actionType === 'password_reset') return <LCommon id="auth.password_reset.button" defaultValue='Reset Password' />;
  if (actionType === 'resend_verification') return <LCommon id="auth.resend_verification.button" defaultValue='Resend Verification Email' />;
  if (actionType === 'magic_link') return <LCommon id="auth.magic_link.button" defaultValue='Sign In with Magic Link' />;
  return null;
};

// Helper to render success message with explicit fallbacks
const renderActionSuccess = (actionType: FAQActionType) => {
  if (actionType === 'password_reset') return <LCommon id="auth.password_reset.success" defaultValue='A password reset link has been sent to your email.' />;
  if (actionType === 'resend_verification') return <LCommon id="auth.resend_verification.success" defaultValue='A verification email has been sent to your inbox.' />;
  if (actionType === 'magic_link') return <LCommon id="auth.magic_link.success" defaultValue="Magic link sent! Check your email." />;
  return null;
};

export const FAQActionDialog = ({
  open,
  onOpenChange,
  actionType,
  defaultEmail = "",
}: FAQActionDialogProps) => {
  const { lCommon } = useLocalizations();
  const [email, setEmail] = useState(defaultEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!actionType || !email.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const redirectUrl = `${window.location.origin}/`;

      switch (actionType) {
        case 'password_reset': {
          // WP bridge: ensure auth.users entry exists before reset
          try {
            await supabase.functions.invoke('wp-password-bridge', {
              body: { email, mode: 'recovery' },
            });
          } catch {
            // Silent fail — proceed with reset regardless
          }
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
          });
          if (error) throw error;
          break;
        }
        case 'resend_verification': {
          const { error } = await supabase.auth.resend({
            type: 'signup',
            email,
            options: {
              emailRedirectTo: redirectUrl,
            },
          });
          if (error) throw error;
          break;
        }
        case 'magic_link': {
          const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
              emailRedirectTo: redirectUrl,
            },
          });
          if (error) throw error;
          break;
        }
      }

      setIsSuccess(true);
      toast.success(lCommon(ACTION_CONFIG[actionType].successKey, { defaultValue: 'Success' }));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : lCommon('errors.generic', { defaultValue: 'Something went wrong.' });
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail(defaultEmail);
    setIsSuccess(false);
    setError(null);
    onOpenChange(false);
  };

  if (!actionType) return null;

  const config = ACTION_CONFIG[actionType];
  const Icon = config.icon;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className={cn(RESPONSIVE.flexRowCenterGap2)}>
            <Icon className="h-5 w-5 text-primary" />
            {renderActionTitle(actionType)}
          </DialogTitle>
          <DialogDescription>
            {renderActionDescription(actionType)}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <CheckCircle2 className="h-12 w-12 text-success" />
            <p className="text-center text-muted-foreground">
              {renderActionSuccess(actionType)}
            </p>
            <Button onClick={handleClose} variant="outline">
              <LCommon id="actions.close" defaultValue='Close' />
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className={cn(SPACING.contentContainer, 'py-4')}>
              <div className={cn(SPACING.formFieldGap)}>
                <Label htmlFor="action-email">
                  <LCommon id="labels.email" defaultValue='Email' />
                </Label>
                <Input
                  id="action-email"
                  type="email"
                  placeholder={lCommon('placeholders.email.placeholder', { defaultValue: 'Enter email' })}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                  maxLength={255}
                  autoComplete="email"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading} className="w-full sm:w-auto">
                <LCommon id="actions.cancel" defaultValue='Cancel' />
              </Button>
              <Button type="submit" disabled={isLoading || !email.trim()} className="w-full sm:w-auto">
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <LCommon id="actions.sending" defaultValue='Sending' />
                  </>
                ) : (
                  renderActionButton(actionType)
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
